import { UserProfile } from 'api/account/models/UserProfile';
import RemindersService from 'api/reminders/RemindersService';
import { ReminderTriggerViewModel, ReminderViewModel } from 'api/reminders/models/ReminderViewModel';
import UsersService from 'api/users/UsersService';
import VehiclesService from 'api/vehicles/VehiclesService';
import IconBack from 'assets/svg/Arrow-grey.svg';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import InputError from 'common/components/inputError/InputError';
import Label from 'common/components/label/Label';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import Utils from 'common/services/Utils';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { LOGGER_LOG_TYPE } from 'Config';
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { Reducers } from 'store/types';
import styles from './ReminderScreen.module.scss';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import { SelectValueLabelDto } from 'api/common/models/SelectValueLabelDto';
import Select from 'common/components/select/Select';
import ReminderCategoriesService from 'api/reminderCategories/ReminderCategoriesService';
import ReminderTriggerForm from './reminderTriggerForm/ReminderTriggerForm';
import SelectController from 'common/components/select/SelectController';
import { UsersSelectItemDto } from 'api/users/models/UserDto';

export type Props = {
    isVehicle?: boolean;
    isUser?: boolean;
    onBackList: () => void;
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const ReminderScreen = ({ isVehicle, isUser, onBackList, ...props }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    // eslint-disable-next-line
    let { vehicleId, userId, itemType, itemId, tabId, type } = useParams<{ vehicleId: string, userId: string, itemType: string, itemId: string, tabId: string, type: string }>();
    const [itemToRemove, setItemToRemove] = useState<ReminderViewModel>();
    const [dialogDeleteItemIsOpen, setDialogDeleteItemIsOpen] = React.useState(false);
    const [reminder, setReminder] = useState<ReminderViewModel | null>(null);
    const [vehicleOptions, setVehicleOptions] = useState<SelectValueLabel[]>([]);
    const [typeOptions, setTypeOptions] = useState<SelectValueLabelDto[]>([]);
    const [vehicle, setVehicle] = useState<VehicleDto | undefined>(undefined);
    const [typeId, setTypeId] = useState<string | undefined>(undefined);
    const [selectecType, setSelectecType] = useState<SelectValueLabel>();
    const [categoryOptions, setCategoryOptions] = useState<SelectValueLabel[]>([]);
    const [criteriaOptions, setCriteriaOptions] = useState<SelectValueLabel[]>([]);
    const [measureOptions, setMeasureOptions] = useState<SelectValueLabel[]>([]);
    const [responsablesOptions, setResponsablesOptions] = useState<SelectValueLabel[]>([]);


    const form = useForm<ReminderViewModel>({ shouldUnregister: false, defaultValues: {} });
    const { handleSubmit, getValues, setValue, control, errors, reset } = form;

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasRemindersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REMINDER_WRITE']);
    const [refreshKey, setRefreshKey] = useState<number>(new Date().getTime());

    const refresh = () => {
        setRefreshKey(new Date().getTime());
    }

    const getData = async () => {

        try {
            Loading.show();
            const __types = [{ value: 'USER', label: t('reminder_categories.types.USER') },
            { value: 'VEHICLE', label: t('reminder_categories.types.VEHICLE') }]
            setTypeOptions(__types);
            setTypeId('VEHICLE') //neste momento apenas posso criar lembretes do tipo veiculos
            setSelectecType(__types[1]);
            setCriteriaOptions([
                { value: 'DATE', label: t('reminders.notifications.criteria.DATE') }]);

            setMeasureOptions([
                { value: 'HOURS', label: t('reminders.notifications.measure.HOURS') },
                { value: 'DAY', label: t('reminders.notifications.measure.DAY') },
                { value: 'YEAR', label: t('reminders.notifications.measure.YEAR') },
                { value: 'MONTH', label: t('reminders.notifications.measure.MONTH') },
                { value: 'WEEK', label: t('reminders.notifications.measure.WEEK') }]);

            const responsables = await UsersService.getAllForCompany();
            const __responsablesOptions = (responsables || []).map((x: UsersSelectItemDto) => ({ value: x.email || '', label: x.realName ? `${x.realName} (${x.email})` : x.email || '' }));
            setResponsablesOptions(__responsablesOptions);

            const vehicles = await VehiclesService.catalog();
            setVehicleOptions(vehicles);

            if (itemId) {
                const result = await RemindersService.getById(itemId);
                if (result === undefined) {
                    addToast(t('common.messages.error_load_info'), { appearance: 'error' });
                    Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't get the reminder with id: ${itemId}`);
                    history.goBack();
                }
                else {
                    if (result && result.triggers?.length > 0) {
                        result.triggers = result.triggers.map((x: ReminderTriggerViewModel) => {
                            const __contacts = x.contacts?.filter(z => !responsables.find((y: UsersSelectItemDto) => { return y.email === z.email }));
                            if (__contacts) {
                                __contacts.forEach(c => {
                                    if (!__responsablesOptions.find((y: SelectValueLabel) => { return y.value === c.email })) {
                                        __responsablesOptions.push({ value: c.email || '', label: c.email || '' });
                                    }
                                });
                            }
                            return {
                                ...x,
                                firstDate: x.firstDate ? new Date(x.firstDate) : null,
                                stopDate: x.stopDate ? new Date(x.stopDate) : null,
                            };
                        });
                    }
                    setResponsablesOptions(__responsablesOptions);
                    reset(result);
                    setReminder(result);
                }
            }
            else {
                if (vehicleId) {
                    changeVehicle({ value: vehicleId, label: '' });
                }
            }
            refresh();
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't get information to create reminder`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }


    const getCategoryByType = async () => {
        try {
            Loading.show();

            const _categories = await ReminderCategoriesService
                .getList({ page: 1, itemsPerPage: 1000, type: selectecType?.value });

            if (_categories && Utils.isStringNullOrEmpty(itemType) ? (type === 'new') : (itemType === 'new')) {
                _categories.items = _categories.items.filter(x => x.isSystem === false);
            }

            const __categories = _categories.items.map(x => ({ value: x.id ?? '', name: x.designation, label: x.isSystem ? t(`reminder_categories.category_types.${x.designation}` as any) : x.designation }));
            setCategoryOptions(__categories);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't get information to create reminder`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }



    const calculateNextDate = (firstDate: Date, beforeValue?: number, beforeMeasure?: string): Date => {
        const date = new Date(firstDate);
        if (beforeValue) {
            switch (beforeMeasure) {
                case 'DAY': date.setDate(date.getDate() - beforeValue); break;
                case 'WEEK': date.setDate(date.getDate() - beforeValue * 7); break;
                case 'MONTH': date.setMonth(date.getMonth() - beforeValue); break;
                case 'YEAR': date.setFullYear(date.getFullYear() - beforeValue); break;
            }
        }
        return date;
    };

    const onSubmit = async (form: ReminderViewModel) => {
        try {
            if (form.triggers?.length > 0) {
                const now = new Date();
                for (const trigger of form.triggers) {
                    if (trigger.criteria === 'DATE' && trigger.firstDate) {
                        const nextDate = calculateNextDate(trigger.firstDate, trigger.beforeDateValue, trigger.beforeDateMeasure);
                        if (nextDate < now) {
                            addToast(t('reminders.notifications.date_in_past_error'), { appearance: 'error' });
                            return;
                        }
                    }
                }
            }

            form.vehicleId = Utils.isStringNullOrEmpty(vehicleId) ? form.vehicleId : vehicleId;
            Loading.show();
            if (form.id) {
                await RemindersService.update(form)
            } else {
                itemId = await RemindersService.create(form)
            }
            Loading.hide();
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            getData();
            if (onBackList) {
                onBackList();
            } {
                history.goBack();
            }

        } catch (error) {
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't update the reminder with id: ${form.id}`, error);
            Loading.hide();
        }
    }

    const showRemoveItemDialog = (item: ReminderViewModel) => {
        setItemToRemove(item);
        setDialogDeleteItemIsOpen(true);
    };

    const removeItem = async () => {
        setDialogDeleteItemIsOpen(false);
        if (!!itemToRemove) {
            try {
                Loading.show();
                await RemindersService.remove(itemToRemove.id ?? '');
                addToast(t('common.messages.record_delete_success'), { appearance: 'success' });

                onBackList && onBackList();
                Loading.hide();
            } catch (error) {
                addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
                Loading.hide();
            }
        }
    };

    const changeVehicle = (data: SelectValueLabel) => {
        const vId = data ? data.value : null;
        setValue('vehicleId', vId);
        getVehicle(vId);
    }

    useEffect(() => {
        getData();
    }, []);


    useEffect(() => {
        if (selectecType) {
            getCategoryByType();
        }
    }, [selectecType, type, itemType]);



    const getVehicle = async (id?: string | null) => {
        try {
            const _vehicle = id ? await VehiclesService.getById(id) : undefined
            setVehicle(_vehicle);
            const opts: SelectValueLabel[] = [
                { value: 'DATE', label: t('reminders.notifications.criteria.DATE') }
            ];
            if (_vehicle?.segmentFeatures?.includes('KM')) {
                opts.push({ value: 'KMS', label: t('reminders.notifications.criteria.KMS') });
            }
            if (_vehicle?.segmentFeatures?.includes('HOURS')) {
                opts.push({ value: 'HOURS', label: t('reminders.notifications.criteria.HOURS') });
            }
            setCriteriaOptions(opts);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get vehicle data`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }



    const renderBody = () => {
        return (
            <>
                <Row>
                    <Col xs={12} md={6} lg={4}>
                        <FormItem>
                            <Label className={styles.label}>{t('common.type')} {Utils.isStringNullOrEmpty(itemType) ? type !== 'details' : itemType !== 'details' ? '*' : ''}</Label>

                            <Controller
                                render={({ onChange, value, ref }) => {
                                    return (
                                        <Select
                                            options={typeOptions}
                                            isDisabled={true}
                                            placeholder={t('common.type')}
                                            onChange={(data: SelectValueLabelDto) => {
                                                onChange(data?.value ?? null);
                                                setSelectecType(data);
                                                setTypeId(data?.value ?? undefined)
                                            }}
                                            ref={ref}
                                            value={typeOptions.find(x => x.value === value) ? { label: typeOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                        />
                                    );
                                }}
                                control={control}
                                name="type"
                                defaultValue={typeId}
                                rules={{ required: true }} />
                        </FormItem>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                        <FormItem>
                            <Label className={styles.label}>{t('reminders.category')} {type !== 'details' ? '*' : ''}</Label>

                            <Controller
                                render={({ onChange, value, ref }) => {
                                    return (
                                        <Select
                                            options={categoryOptions}
                                            isDisabled={(Utils.isStringNullOrEmpty(itemType) ? (type === 'details' || type === 'edit') : (itemType === 'details' || itemType === 'edit'))}
                                            placeholder={t('reminders.category')}
                                            onChange={(data: SelectValueLabel) => {
                                                onChange(data?.value ?? null);
                                                setValue('categoryId', data?.value ?? undefined)
                                            }}
                                            ref={ref}
                                            value={categoryOptions.find(x => x.value === value) ? { label: categoryOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                        />
                                    );
                                }}
                                control={control}
                                name="categoryId"
                                rules={{ required: true }} />
                        </FormItem>
                        <InputError error={errors.categoryId} />
                    </Col>

                    {!vehicleId && <Col xs={12} md={6} lg={4}>
                        <FormItem>
                            <Label className={styles.label}>{t('vehicle.vehicle')} {type !== 'details' ? '*' : ''}</Label>
                            <SelectController
                                form={form}
                                isDisabled={(Utils.isStringNullOrEmpty(itemType) ? type === 'details' : itemType === 'details')}
                                placeholder={t('vehicle.vehicle')}
                                name="vehicleId"
                                options={vehicleOptions}
                                rules={{ required: true }}
                                onChangeSelect={(data: SelectValueLabel) => {
                                    changeVehicle(data);
                                }} />

                            <InputError error={errors.vehicleId} />
                        </FormItem>
                    </Col>}
                </Row>

                <Row>
                    <ReminderTriggerForm form={form}
                        control={1}
                        responsablesOptions={responsablesOptions}
                        measureOptions={measureOptions}
                        criteriaOptions={criteriaOptions}
                        isDetails={(Utils.isStringNullOrEmpty(itemType) ? type === 'details' : itemType === 'details')}
                        onCreateResponsableOption={(data: any) => { setResponsablesOptions([...responsablesOptions, { value: data, label: data }]) }}
                    />
                </Row>
                {/* <Row>
                    <ReminderContactForm form={form} isDetails={(Utils.isStringNullOrEmpty(itemType) ? type === 'details' : itemType === 'details')} />
                </Row> */}
            </>
        );
    }

    const renderButtons = () => {
        const showButtonBasedOnPolicy = (Utils.isStringNullOrEmpty(itemType) ? type !== 'details' : itemType !== 'details') && hasRemindersWritePolicy;
        const showEditOrRemoveButton = (Utils.isStringNullOrEmpty(itemType) ? type === 'details' : itemType === 'details') && hasRemindersWritePolicy && reminder?.active;

        return (
            <>
                <div className={styles.buttonsContainer}>
                    <Button
                        preset={'secondary'}
                        type="button"
                        onClick={() => {
                            (isVehicle || isUser) ? onBackList() : history.goBack()
                        }}
                        text={t('common.cancel')}
                    />
                    {showEditOrRemoveButton &&
                        <Button
                            type="button"
                            text={t('common.remove')}
                            preset={'danger'}
                            onClick={() => showRemoveItemDialog({ id: reminder?.id } as ReminderViewModel)} />
                    }
                    {showEditOrRemoveButton &&
                        <Button
                            type={'button'}
                            text={t('common.edit')}
                            onClick={() => {
                                if (Utils.isStringNullOrEmpty(tabId)) {
                                    history.push(`/reminders/edit/${itemId}`);
                                } else {
                                    history.push(`/${isUser ? 'users' : 'vehicles'}/details/${isUser ? userId : vehicleId}/${tabId}/edit/${itemId}`);
                                }
                            }}
                        />}
                    {showButtonBasedOnPolicy &&
                        <Button
                            type={'submit'}
                            text={t('common.save')}
                        />}

                    <QuestionYesNo message={t('common.messages.remove_record')}
                        isVisible={dialogDeleteItemIsOpen}
                        onYes={() => removeItem()}
                        onNo={() => setDialogDeleteItemIsOpen(false)} />
                </div>
            </>
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.fullHeight} key={'form-' + refreshKey}>
            {(isVehicle || isUser) &&
                <div {...props} className={styles.fullHeight}>
                    <Row className={styles.fullHeight} style={{ margin: 0 }}>
                        <Col xs={12}>
                            <div className={styles.content}>
                                <div className={styles.header}>
                                    <img src={IconBack} className={styles.icon} onClick={() => {
                                        if (!!reminder) { reset(reminder); }
                                        onBackList()
                                    }} /> {t('reminders.title')}
                                </div>
                                <div>
                                    {renderBody()}
                                </div>
                            </div>
                            <Row>
                                {renderButtons()}
                            </Row>
                        </Col>
                    </Row>
                </div>

            }

            {(!isVehicle && !isUser) &&
                <div>
                    <Row>
                        <Col xs={12}>
                            {(Boolean(reminder) && !reminder?.active) &&
                                <div className={styles.header}>
                                    <span className={styles.messageDeleted}>{t('reminders.reminder_deleted')}</span>
                                </div>
                            }
                            {renderBody()}
                        </Col>
                    </Row>
                    <Row>
                        {renderButtons()}
                    </Row>
                </div>
            }

        </form >
    );
}

export default ReminderScreen;